/**
 * Minimal STL geometry loader.
 * Supports binary STL format (the standard output of most 3D capture pipelines).
 *
 * Binary STL layout:
 *   80 bytes  – header (ignored)
 *    4 bytes  – uint32 triangle count
 *   For each triangle:
 *     12 bytes – normal vector  (3 × float32)
 *     36 bytes – 3 vertices     (3 × 3 × float32)
 *      2 bytes – attribute byte count (ignored)
 */

export interface StlGeometry {
    positions: Float32Array; // flat array: x0,y0,z0, x1,y1,z1, …
    normals: Float32Array;   // one normal per vertex (flat shading)
    indices: Uint32Array;    // sequential: 0,1,2, 3,4,5, …
}

/** Parse a binary STL ArrayBuffer and return raw geometry arrays. */
export function parseBinaryStl(buffer: ArrayBuffer): StlGeometry {
    const view = new DataView(buffer);
    const triangleCount = view.getUint32(80, true /* little-endian */);

    const vertexCount = triangleCount * 3;
    const positions = new Float32Array(vertexCount * 3);
    const normals   = new Float32Array(vertexCount * 3);
    const indices   = new Uint32Array(vertexCount);

    let offset = 84; // skip 80-byte header + 4-byte count

    for (let t = 0; t < triangleCount; t++) {
        // Read face normal
        const nx = view.getFloat32(offset,      true);
        const ny = view.getFloat32(offset +  4, true);
        const nz = view.getFloat32(offset +  8, true);
        offset += 12;

        // Read 3 vertices
        for (let v = 0; v < 3; v++) {
            const idx = (t * 3 + v) * 3;
            positions[idx]     = view.getFloat32(offset,     true);
            positions[idx + 1] = view.getFloat32(offset + 4, true);
            positions[idx + 2] = view.getFloat32(offset + 8, true);
            normals[idx]     = nx;
            normals[idx + 1] = ny;
            normals[idx + 2] = nz;
            indices[t * 3 + v] = t * 3 + v;
            offset += 12;
        }

        offset += 2; // attribute byte count
    }

    return { positions, normals, indices };
}

/** Fetch an STL file from `url` and return its geometry. */
export async function loadStl(url: string): Promise<StlGeometry> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`STL fetch failed (${response.status}): ${url}`);
    }
    return parseBinaryStl(await response.arrayBuffer());
}
